// i18n — "botMind" cluster: the Bot's Knowledge console (`/bot-mind`).
//
// The route and the `botMind*` key prefix keep their original names so links and
// keys stay stable; the user-facing name of this screen is "Bot's Knowledge".
//
// Every user-visible string on the four memory-tier tabs (Tenant Mind, Agent
// Brain, Agent Core, Agent Memories) plus the shared TierNotesPanel /
// BrainEditor components. Spread into the central LABELS in labels.ts.
//
// Key prefix is `botMind*` throughout so the spread can never shadow a key
// from another cluster (spread order silently wins on duplicates).
//
// Rules (same as every other cluster):
//  - DISPLAY ONLY. Never feed a label back to an API. The wire values
//    (`[proposed:{form}]` prefixes, note text, form names) are untouched.
//  - `{n}` / `{x}` placeholders are substituted by the caller via .replace().
export const BOT_MIND_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Page shell — title, description, bot selector, tabs
  // ═══════════════════════════════════════════════════════════════════════
  botMindTitle: { en: "Bot's Knowledge", he: 'ידע הבוט' },
  botMindDescription: {
    en: "Everything the bot knows about how to behave, across all four memory tiers. Suggestions the system learned on its own are inert until you approve them here.",
    he: 'כל מה שהבוט יודע על אופן ההתנהגות שלו, בכל ארבע שכבות הזיכרון. הצעות שהמערכת למדה בעצמה אינן פעילות עד שתאשר אותן כאן.',
  },
  botMindNoTenant: {
    en: 'Select a tenant to view its bots.',
    he: 'בחר ארגון כדי לצפות בבוטים שלו.',
  },
  botMindSelectBotLabel: { en: 'Bot', he: 'בוט' },
  botMindSelectBotAria: { en: 'Select the bot whose mind you are editing', he: 'בחר את הבוט שאת מוחו אתה עורך' },
  botMindBotsLoading: { en: 'Loading bots…', he: 'טוען בוטים…' },
  botMindBotsLoadFailed: {
    en: 'Could not load this tenant’s bots. Please try again.',
    he: 'לא ניתן לטעון את הבוטים של הארגון. נסה שוב.',
  },
  botMindNoBots: {
    en: 'This tenant has no bots yet. Create an agent configuration first — Tenant Mind below still applies to every bot.',
    he: 'לארגון זה אין עדיין בוטים. צור תצורת סוכן קודם — מוח הארגון שלמטה חל בכל מקרה על כל בוט.',
  },
  botMindNoBotSelected: {
    en: 'Pick a bot above to view this tier — it is stored per bot.',
    he: 'בחר בוט למעלה כדי לצפות בשכבה הזו — היא נשמרת לכל בוט בנפרד.',
  },
  botMindTabsAria: { en: 'Memory tiers', he: 'שכבות זיכרון' },
  botMindTabTenantMind: { en: 'Tenant Mind', he: 'מוח הארגון' },
  botMindTabBrain: { en: 'Agent Brain', he: 'מוח הסוכן' },
  botMindTabCore: { en: 'Agent Core', he: 'ליבת הסוכן' },
  botMindTabMemories: { en: 'Agent Memories', he: 'זיכרונות הסוכן' },
  botMindLoading: { en: 'Loading…', he: 'טוען…' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Tier descriptions (one line each, above the notes panel)
  // ═══════════════════════════════════════════════════════════════════════
  botMindTenantMindDesc: {
    en: 'Tenant-wide — shared by every bot in this tenant. Not per bot.',
    he: 'ברמת הארגון — משותף לכל הבוטים בארגון. לא לכל בוט בנפרד.',
  },
  botMindBrainDesc: {
    en: 'Per bot, hand-authored, permanent. This is what the bot always reads.',
    he: 'לכל בוט, נכתב ידנית, קבוע. זה מה שהבוט קורא תמיד.',
  },
  botMindCoreDesc: {
    en: 'Per bot, machine-curated by the learning batch. Suggestions here are inert until approved.',
    he: 'לכל בוט, נאסף אוטומטית על ידי תהליך הלמידה. הצעות כאן אינן פעילות עד לאישור.',
  },
  botMindMemoriesDesc: {
    en: 'Per bot, per end customer — short-term. One row per customer the bot has spoken with.',
    he: 'לכל בוט ולכל לקוח קצה — זיכרון קצר טווח. שורה אחת לכל לקוח שהבוט שוחח איתו.',
  },
  botMindTenantReadOnlyHint: {
    en: 'Identity, brand voice and escalation rules are edited on the Tenant Mind settings surface. Shown here so you can see where an approved suggestion lands.',
    he: 'זהות, קול המותג וכללי הסלמה נערכים במסך הגדרות מוח הארגון. מוצגים כאן כדי שתראה לאן מגיעה הצעה שאושרה.',
  },
  botMindTenantIdentity: { en: 'Identity', he: 'זהות' },
  botMindTenantBrandVoice: { en: 'Brand voice', he: 'קול המותג' },
  botMindTenantProhibited: { en: 'Prohibited topics', he: 'נושאים אסורים' },
  botMindTenantEscalationRules: { en: 'Escalation rules', he: 'כללי הסלמה' },
  botMindEmptyValue: { en: '— not set —', he: '— לא הוגדר —' },
  botMindLastPromotion: { en: 'Last learning run: {x}', he: 'הרצת הלמידה האחרונה: {x}' },

  // ═══════════════════════════════════════════════════════════════════════
  //  TierNotesPanel — proposals group (the review centrepiece)
  // ═══════════════════════════════════════════════════════════════════════
  botMindProposalsHeading: {
    en: 'Suggestions awaiting your review ({n})',
    he: 'הצעות הממתינות לאישורך ({n})',
  },
  botMindProposalsIntro: {
    en: 'The bot DELIBERATELY ignores these until you approve them. Approve promotes a suggestion into the bot’s permanent behavior; Discard removes it.',
    he: 'הבוט מתעלם מהן במכוון עד לאישורך. אישור מקדם את ההצעה להתנהגות קבועה של הבוט; דחייה מסירה אותה.',
  },
  botMindFormBadgeFallback: { en: 'note', he: 'הערה' },
  botMindAdoptDestLimitation: {
    en: 'Approving adds this to the bot’s Limitations (Agent Brain).',
    he: 'אישור יוסיף את זה למגבלות הבוט (מוח הסוכן).',
  },
  botMindAdoptDestMethod: {
    en: 'Approving adds this to the bot’s Methods (Agent Brain).',
    he: 'אישור יוסיף את זה לשיטות העבודה של הבוט (מוח הסוכן).',
  },
  botMindAdoptDestEscalationRule: {
    en: 'Approving adds this to the tenant’s Escalation rules (Tenant Mind).',
    he: 'אישור יוסיף את זה לכללי ההסלמה של הארגון (מוח הארגון).',
  },
  botMindAdoptDestTenantVoice: {
    en: 'Approving folds this into the tenant’s Brand voice (Tenant Mind).',
    he: 'אישור ישלב את זה בקול המותג של הארגון (מוח הארגון).',
  },
  // generalNote means the classifier could NOT place it. It is filed as a plain note, not
  // asserted as a method the bot follows — which is where these used to land.
  botMindAdoptDestNote: {
    en: 'Approving files this under the tenant’s Notes (Tenant Mind).',
    he: 'אישור יתייק את זה תחת ההערות של הארגון (מוח הארגון).',
  },
  botMindApprove: { en: 'Approve', he: 'אשר' },
  botMindApproving: { en: 'Approving…', he: 'מאשר…' },
  botMindDiscard: { en: 'Discard', he: 'דחה' },
  botMindDiscarding: { en: 'Discarding…', he: 'דוחה…' },
  botMindApproveAria: { en: 'Approve {x} suggestion: {n}', he: 'אשר הצעת {x}: {n}' },
  botMindDiscardAria: { en: 'Discard {x} suggestion: {n}', he: 'דחה הצעת {x}: {n}' },
  botMindProposalsNeedBot: {
    en: 'Pick a bot above to approve or discard tenant-wide suggestions.',
    he: 'בחר בוט למעלה כדי לאשר או לדחות הצעות ברמת הארגון.',
  },
  botMindReadOnlyNotice: {
    en: 'You can view the bot’s knowledge but not change it — editing needs the memory management permission.',
    he: 'אתה יכול לצפות בידע הבוט אך לא לשנות אותו — לעריכה נדרשת הרשאת ניהול זיכרון.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  TierNotesPanel — ordinary notes, add / edit / delete
  // ═══════════════════════════════════════════════════════════════════════
  botMindNotesHeading: { en: 'Notes ({n})', he: 'הערות ({n})' },
  botMindNoNotes: { en: 'No notes in this tier yet.', he: 'אין עדיין הערות בשכבה הזו.' },
  botMindPriorityHigh: { en: 'high', he: 'גבוה' },
  botMindPriorityDemoted: { en: 'demoted', he: 'הורד' },
  botMindNoteFromCustomer: { en: 'from customer …{x}', he: 'מלקוח …{x}' },

  botMindAddNoteLabel: { en: 'Add a note', he: 'הוסף הערה' },
  botMindAddNotePlaceholder: {
    en: 'Write one behavior or fact the bot should remember…',
    he: 'כתוב התנהגות או עובדה אחת שהבוט צריך לזכור…',
  },
  botMindAddNote: { en: 'Add note', he: 'הוסף הערה' },
  botMindAddingNote: { en: 'Adding…', he: 'מוסיף…' },
  botMindNoteRequired: { en: 'Write something first.', he: 'כתוב משהו קודם.' },
  botMindNoteTooLong: { en: 'Keep a note under 2000 characters.', he: 'הערה חייבת להיות עד 2000 תווים.' },

  botMindEditNote: { en: 'Edit', he: 'ערוך' },
  botMindEditNoteAria: { en: 'Edit note: {n}', he: 'ערוך הערה: {n}' },
  botMindEditNoteFieldAria: { en: 'Note text', he: 'תוכן ההערה' },
  botMindSaveNote: { en: 'Save note', he: 'שמור הערה' },
  botMindSavingNote: { en: 'Saving…', he: 'שומר…' },
  botMindCancelEdit: { en: 'Cancel', he: 'בטל' },

  botMindDeleteNote: { en: 'Delete', he: 'מחק' },
  botMindDeleteNoteAria: { en: 'Delete note: {n}', he: 'מחק הערה: {n}' },
  botMindDeleteNoteTitle: { en: 'Delete this note?', he: 'למחוק את ההערה?' },
  botMindDeleteNoteMessage: {
    en: 'The bot will stop using this note immediately. This cannot be undone.',
    he: 'הבוט יפסיק להשתמש בהערה הזו מיד. לא ניתן לבטל.',
  },

  // Stale-write path — the note changed server-side (404 note_not_found).
  botMindNoteChanged: {
    en: 'This note changed since the page loaded — reloading the latest version…',
    he: 'ההערה הזו השתנתה מאז שהדף נטען — טוען את הגרסה העדכנית…',
  },
  botMindResourceNote: { en: 'note', he: 'הערה' },
  botMindServiceName: { en: 'Agent memory service', he: 'שירות זיכרון הסוכן' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Agent Core — conflicts with the hand-authored Brain
  // ═══════════════════════════════════════════════════════════════════════
  botMindConflictsHeading: { en: 'Conflicts with the Brain ({n})', he: 'התנגשויות עם מוח הסוכן ({n})' },
  botMindConflictsIntro: {
    en: 'The learning batch wanted to promote these but they contradict what you hand-authored in the Brain. Update the Brain or discard them.',
    he: 'תהליך הלמידה רצה לקדם את אלה אך הם מתנגשים עם מה שכתבת ידנית במוח הסוכן. עדכן את מוח הסוכן או דחה אותם.',
  },
  botMindConflictCoreWants: { en: 'Core wants', he: 'הליבה מבקשת' },
  botMindConflictBrainHas: { en: 'Brain has ({x})', he: 'במוח הסוכן ({x})' },

  // ═══════════════════════════════════════════════════════════════════════
  //  BrainEditor — whole-document, hand-authored, permanent
  // ═══════════════════════════════════════════════════════════════════════
  botMindBrainWarning: {
    en: 'The Brain is hand-authored and permanent — the bot reads all of it on every turn. Nothing here expires or gets cleaned up automatically. Saving replaces the whole document.',
    he: 'מוח הסוכן נכתב ידנית והוא קבוע — הבוט קורא את כולו בכל תור. שום דבר כאן לא מתיישן ולא מנוקה אוטומטית. שמירה מחליפה את המסמך כולו.',
  },
  botMindBrainInstructionLabel: { en: 'Agent instruction', he: 'הנחיית הסוכן' },
  botMindBrainInstructionHint: {
    en: 'The bot’s standing instruction — who it is and how it should work.',
    he: 'ההנחיה הקבועה של הבוט — מי הוא וכיצד עליו לעבוד.',
  },
  botMindBrainInstructionTooLong: {
    en: 'Keep the instruction under 8000 characters.',
    he: 'ההנחיה חייבת להיות עד 8000 תווים.',
  },
  botMindBrainLimitations: { en: 'Limitations', he: 'מגבלות' },
  botMindBrainLimitationsHint: {
    en: 'Things the bot must never do or promise.',
    he: 'דברים שהבוט לעולם לא יעשה או יבטיח.',
  },
  botMindBrainMethods: { en: 'Methods', he: 'שיטות עבודה' },
  botMindBrainMethodsHint: {
    en: 'How the bot should handle recurring situations.',
    he: 'איך הבוט אמור לטפל במצבים חוזרים.',
  },
  botMindBrainMenus: { en: 'Menus', he: 'תפריטים' },
  botMindBrainMenusHint: {
    en: 'Fixed option lists the bot may offer the customer.',
    he: 'רשימות אפשרויות קבועות שהבוט יכול להציע ללקוח.',
  },
  botMindBrainSave: { en: 'Save Brain', he: 'שמור את מוח הסוכן' },
  botMindBrainSaving: { en: 'Saving…', he: 'שומר…' },
  botMindBrainSaved: { en: 'Saved.', he: 'נשמר.' },
  botMindBrainResource: { en: 'agent brain', he: 'מוח הסוכן' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Agent Memories — per end-customer rows
  // ═══════════════════════════════════════════════════════════════════════
  botMindMemoriesPrivacyNote: {
    en: 'These are END-CUSTOMER memories. Treat them as customer data: only what the API returns is shown, and identifiers are masked.',
    he: 'אלה זיכרונות של לקוחות קצה. התייחס אליהם כאל נתוני לקוח: מוצג רק מה שה-API מחזיר, והמזהים מוסתרים.',
  },
  botMindMemoriesEmpty: {
    en: 'No customer memories yet. A row appears here after the bot’s first conversation with a customer.',
    he: 'אין עדיין זיכרונות לקוח. שורה תופיע כאן לאחר השיחה הראשונה של הבוט עם לקוח.',
  },
  botMindMemoriesCount: { en: 'Showing {n} customers, most recently active first.', he: 'מוצגים {n} לקוחות, הפעילים לאחרונה תחילה.' },
  botMindMemoryAnonymous: { en: 'Anonymous customer', he: 'לקוח אנונימי' },
  botMindMemoryIdentified: { en: 'Identified customer', he: 'לקוח מזוהה' },
  botMindMemoryLastTurn: { en: 'last turn {x}', he: 'תור אחרון {x}' },
  botMindMemoryNoteCount: { en: '{n} notes', he: '{n} הערות' },
  botMindMemoryNoSummary: { en: 'No summary yet.', he: 'אין עדיין תקציר.' },
  botMindMemoryExpandAria: { en: 'Show notes for customer …{x}', he: 'הצג הערות עבור לקוח …{x}' },
  botMindMemoryCollapseAria: { en: 'Hide notes for customer …{x}', he: 'הסתר הערות עבור לקוח …{x}' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Page shell — one-time "what are these four tabs?" primer
  // ═══════════════════════════════════════════════════════════════════════
  botMindTabsPrimer: {
    en: 'Tenant Mind = rules for every bot · Agent Brain = this bot’s fixed instructions · Agent Core = what the bot has learned on its own · Agent Memories = what the bot remembers about each customer.',
    he: 'מוח הארגון = כללים לכל הבוטים · מוח הסוכן = ההנחיות הקבועות של הבוט הזה · ליבת הסוכן = מה שהבוט למד בעצמו · זיכרונות הסוכן = מה שהבוט זוכר על כל לקוח.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Friendly labels for raw behaviorForm / behaviorScope wire values shown
  //  on the Learning page's activity table (labels.knowledge.ts is locked to
  //  a single key during this pass, so these small additions live here —
  //  they merge into the same lookup regardless of source file).
  // ═══════════════════════════════════════════════════════════════════════
  botMindFormLimitation: { en: 'Limitation', he: 'מגבלה' },
  botMindFormEscalationRule: { en: 'Escalation rule', he: 'כלל הסלמה' },
  botMindFormTenantVoice: { en: 'Brand voice', he: 'קול המותג' },
  botMindFormMethod: { en: 'Method', he: 'שיטת עבודה' },
  botMindScopeAgent: { en: 'this bot only', he: 'הבוט הזה בלבד' },
  botMindScopeTenant: { en: 'every bot', he: 'כל בוט' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Learning page — small additions (same lock reason as above)
  // ═══════════════════════════════════════════════════════════════════════
  learningMigrateSlowNote: {
    en: 'Still working — large histories can take a while. It\'s safe to leave this page; the progress picks back up when you return.',
    he: 'עדיין בעבודה — היסטוריות גדולות עשויות לקחת זמן. אפשר לצאת מהדף בבטחה; ההתקדמות תמשיך כשתחזור.',
  },
  learningRunTimeoutSuffix: {
    en: ' · {timeouts} agent{s} still running',
    he: ' · {timeouts} סוכנים עדיין פועלים',
  },
  learningRunTimeoutWarning: {
    en: '{timeouts} agent run{s} still running server-side — check back before retrying',
    he: 'ריצות של {timeouts} סוכנים עדיין פועלות בצד השרת — בדוק שוב לפני ניסיון חוזר',
  },
  learningActivityWhatIsThis: {
    en: 'Each row is one thing a bot tried to learn. We show whether it was saved, and why not, if not.',
    he: 'כל שורה היא דבר אחד שבוט ניסה ללמוד. אנו מציגים אם הוא נשמר, ומדוע לא, אם לא נשמר.',
  },
} satisfies Record<string, { en: string; he: string }>;
